import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSecurity } from '../../../../SecurityContext';
import PersonalHubFactionReputationIcon from '../../../../components/icons/PersonalHubFactionReputationIcon';
import PersonalHubDashboardIcon from '../../../../components/icons/PersonalHubDashboardIcon';
import PersonalHubHangarIcon from '../../../../components/icons/PersonalHubHangarIcon';
import PersonalHubBlueprintIcon from '../../../../components/icons/PersonalHubBlueprintIcon';
import {
  PERSONAL_HUB_RAIL_ITEMS,
  PERSONAL_HUB_SECTION_BLUEPRINTS,
  PERSONAL_HUB_SECTION_FACTIONS_REPUTATIONS,
  PERSONAL_HUB_SECTION_DASHBOARD,
  PERSONAL_HUB_SECTION_HANGAR,
  personalHubRailBtnModifier,
} from './personalHubRailConfig';

function RailIcon({ sectionId, className }) {
  if (sectionId === PERSONAL_HUB_SECTION_DASHBOARD) {
    return <PersonalHubDashboardIcon className={className} />;
  }
  if (sectionId === PERSONAL_HUB_SECTION_HANGAR) {
    return <PersonalHubHangarIcon className={className} />;
  }
  if (sectionId === PERSONAL_HUB_SECTION_FACTIONS_REPUTATIONS) {
    return <PersonalHubFactionReputationIcon className={className} />;
  }
  if (sectionId === PERSONAL_HUB_SECTION_BLUEPRINTS) {
    return <PersonalHubBlueprintIcon className={className} />;
  }
  return null;
}

export default function PersonalHubSideRail({
  activeSection,
  onSelectSection,
  onPrefetchSection,
}) {
  const { t } = useTranslation();
  const { subjectHasPermission, isViewAsActive, viewAsLoading, permissionsVersion } =
    useSecurity();

  const canAccessSection = useCallback(
    (item) => subjectHasPermission(item.resourceKey, item.action),
    [subjectHasPermission, permissionsVersion]
  );

  const permissionsPending = isViewAsActive && viewAsLoading;

  const sectionLabel = useCallback(
    (item) => (item.labelKey ? t(item.labelKey) : item.id),
    [t]
  );

  return (
    <nav
      className="personal-hub-rail"
      aria-label={t('personalhub')}
      data-resource="private.navbar.personalhub"
      data-action="read"
      data-description="Personal hub — section rail"
    >
      <ul className="personal-hub-rail__list">
        {PERSONAL_HUB_RAIL_ITEMS.map((item) => {
          const isLocked = !permissionsPending && !canAccessSection(item);
          const isActive = !isLocked && activeSection === item.id;
          const label = sectionLabel(item);
          const lockedMessage = item.lockedMessageKey ? t(item.lockedMessageKey) : '';
          const tooltipId = `personal-hub-rail-tip-${item.id}`;

          return (
            <li key={item.id} className="personal-hub-rail__item-wrap">
              <button
                type="button"
                className={[
                  'personal-hub-rail__btn',
                  personalHubRailBtnModifier(item.id),
                  isActive ? 'personal-hub-rail__btn--active' : '',
                  isLocked ? 'personal-hub-rail__btn--locked' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                aria-label={isLocked ? `${label} — ${lockedMessage}` : label}
                aria-describedby={tooltipId}
                aria-current={isActive ? 'page' : undefined}
                aria-disabled={isLocked || permissionsPending || undefined}
                data-resource={item.resourceKey}
                data-action={item.action}
                data-description={item.description}
                onMouseEnter={() => {
                  if (isLocked || permissionsPending || !onPrefetchSection) return;
                  onPrefetchSection(item.id);
                }}
                onFocus={() => {
                  if (isLocked || permissionsPending || !onPrefetchSection) return;
                  onPrefetchSection(item.id);
                }}
                onClick={() => {
                  if (isLocked || permissionsPending) return;
                  onPrefetchSection?.(item.id);
                  onSelectSection(item.id);
                }}
              >
                <RailIcon sectionId={item.id} className="personal-hub-rail__icon" />
                <span
                  id={tooltipId}
                  className={[
                    'personal-hub-rail__tooltip',
                    isLocked ? 'personal-hub-rail__tooltip--locked' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  role="tooltip"
                >
                  <span className="personal-hub-rail__tooltip-icon" aria-hidden>
                    <RailIcon
                      sectionId={item.id}
                      className="personal-hub-rail__tooltip-icon-svg"
                    />
                  </span>
                  <span className="personal-hub-rail__tooltip-copy">
                    <span className="personal-hub-rail__tooltip-label">{label}</span>
                    {isLocked && lockedMessage ? (
                      <span className="personal-hub-rail__tooltip-lock-msg">
                        {lockedMessage}
                      </span>
                    ) : null}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
